(() => {
  'use strict';

  if (window.__miniamiNewsletterRuntime) {
    window.__miniamiNewsletterRuntime.initializeRoot(document);
    return;
  }

  const STATES = new Set(['idle', 'loading', 'validation', 'already-subscribed', 'network-error', 'error', 'success']);
  const RETRY_STATES = new Set(['network-error', 'error']);

  const getForm = (root) => {
    if (root.matches('form')) return root;
    return root.closest('form') || root.querySelector('form');
  };

  const getStateRoot = (form) => form.querySelector('[data-newsletter-state]') || form;

  const getStateMessage = (form) => form.querySelector('[data-newsletter-state-message]');

  const getMessages = (form, stateRoot) => ({
    idle: stateRoot.dataset.newsletterMessageIdle || form.dataset.newsletterMessageIdle || '',
    loading: stateRoot.dataset.newsletterMessageLoading || form.dataset.newsletterMessageLoading || '',
    validation: stateRoot.dataset.newsletterMessageValidation || form.dataset.newsletterMessageValidation || '',
    'already-subscribed': stateRoot.dataset.newsletterMessageAlreadySubscribed || form.dataset.newsletterMessageAlreadySubscribed || '',
    'network-error': stateRoot.dataset.newsletterMessageNetworkError || form.dataset.newsletterMessageNetworkError || '',
    error: stateRoot.dataset.newsletterMessageError || form.dataset.newsletterMessageError || '',
    success: stateRoot.dataset.newsletterMessageSuccess || form.dataset.newsletterMessageSuccess || '',
    retry: stateRoot.dataset.newsletterMessageRetry || form.dataset.newsletterMessageRetry || '',
  });

  const preserveEmailValue = (form) => form.querySelector('input[type="email"]')?.value || '';

  const syncVisibleState = (form, state) => {
    const stateRoot = getStateRoot(form);
    const formView = form.querySelector('[data-newsletter-form-view]');
    const successView = form.querySelector('[data-newsletter-success-view]');
    const subscriber = form.querySelector('[data-newsletter-subscriber]');
    const input = form.querySelector('input[type="email"]');
    const subscriberPrefix = stateRoot.dataset.newsletterSubscriberPrefix || '';
    const isSuccess = state === 'success';
    if (formView) {
      formView.hidden = isSuccess;
      formView.setAttribute('aria-hidden', isSuccess ? 'true' : 'false');
    }
    if (successView) {
      successView.hidden = !isSuccess;
      successView.setAttribute('aria-hidden', isSuccess ? 'false' : 'true');
    }
    if (isSuccess && subscriber && input?.value && !subscriber.textContent.trim()) {
      subscriber.textContent = [subscriberPrefix, input.value].filter(Boolean).join(' ');
    }
    stateRoot.dataset.newsletterState = state;
  };

  const setState = (form, state, message) => {
    const stateRoot = getStateRoot(form);
    const stateMessage = getStateMessage(form);
    const submit = form.querySelector('[data-newsletter-submit], button[type="submit"]');
    const retry = form.querySelector('[data-newsletter-retry]');
    const messages = getMessages(form, stateRoot);
    const resolvedState = STATES.has(state) ? state : 'error';
    const resolvedMessage = message || messages[resolvedState] || messages.error;

    stateRoot.dataset.newsletterState = resolvedState;
    form.dataset.newsletterState = resolvedState;
    form.setAttribute('aria-busy', resolvedState === 'loading' ? 'true' : 'false');
    if (stateMessage) {
      stateMessage.dataset.newsletterState = resolvedState;
      stateMessage.textContent = resolvedMessage;
    }
    if (submit) submit.disabled = resolvedState === 'loading';
    if (retry) retry.hidden = !RETRY_STATES.has(resolvedState);
    syncVisibleState(form, resolvedState);
  };

  const parseProviderState = async (form, response) => {
    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const returnedForm = form.id ? documentFragment.getElementById(form.id) : documentFragment.querySelector('form');
    const returnedRoot = returnedForm?.querySelector('[data-newsletter-state]') || returnedForm;
    const returnedMessage = returnedForm?.querySelector('[data-newsletter-state-message], .form__message, .errors, [role="status"]')
      || returnedRoot?.querySelector?.('[data-newsletter-state-message], .form__message, .errors, [role="status"]');
    const state = returnedRoot?.dataset.newsletterState || returnedForm?.dataset.newsletterState;
    const providerMessage = returnedMessage?.textContent.trim() || '';
    if (STATES.has(state) && !['idle', 'loading', 'validation', 'network-error'].includes(state)
      && (state !== 'success' || response.ok)) {
      return { state, message: providerMessage };
    }
    if (providerMessage) {
      return { state: 'error', message: providerMessage };
    }
    if (!STATES.has(state) || ['idle', 'loading', 'validation', 'network-error'].includes(state)) {
      throw new Error('newsletter provider response did not expose a final state');
    }
    throw new Error('newsletter provider response did not expose a final state');
  };

  const initializedForms = new WeakMap();
  const abortRequest = (controller) => controller.abort();

  const getForms = (root) => {
    if (!root) return [];
    const candidates = [];
    if (root.matches?.('form, [data-newsletter-form]')) candidates.push(root);
    root.querySelectorAll?.('form.newsletter-form, form[data-newsletter-form], [data-newsletter-form]').forEach((candidate) => {
      candidates.push(candidate);
    });
    return [...new Set(candidates.map(getForm).filter(Boolean))];
  };

  const init = (form) => {
    if (!form || initializedForms.has(form)) return;
    const stateRoot = getStateRoot(form);
    const nativeSubmit = stateRoot.dataset.newsletterEnhance === 'native';
    const stateMessage = getStateMessage(form);
    const messages = getMessages(form, stateRoot);
    const input = form.querySelector('input[type="email"]');
    const retry = form.querySelector('[data-newsletter-retry]');
    const serverState = stateRoot.dataset.newsletterState || 'idle';
    const initialState = serverState === 'loading' ? 'idle' : serverState;
    const controller = new AbortController();
    let disposed = false;
    let pending = false;

    if (stateMessage && !stateMessage.textContent.trim() && messages[initialState]) {
      stateMessage.textContent = messages[initialState];
    }

    const handleRetry = () => {
      pending = false;
      form.requestSubmit();
    };
    const handlePageShow = () => {
      if (!nativeSubmit || !pending || disposed) return;
      pending = false;
      setState(form, 'idle', messages.idle);
    };
    const handleInput = () => {
      if (input) input.removeAttribute('aria-invalid');
      const currentState = getStateRoot(form).dataset.newsletterState;
      if (currentState === 'validation') setState(form, 'idle', messages.idle);
    };
    const handleInvalid = (event) => {
      if (!input || event.target !== input) return;
      input.setAttribute('aria-invalid', 'true');
      setState(form, 'validation', messages.validation);
    };
    const handleSubmit = async (event) => {
      if (disposed || controller.signal.aborted) return;
      // Shopify owns the customer form transport, including captcha and redirects.
      // Keep it native; never infer a subscriber from a fetched /contact page.
      if (nativeSubmit) {
        if (pending && event.isTrusted) {
          event.preventDefault();
          return;
        }
        if (!input || !input.checkValidity()) {
          event.preventDefault();
          if (input) input.setAttribute('aria-invalid', 'true');
          setState(form, 'validation', messages.validation);
          input?.focus();
          return;
        }
        if (!event.defaultPrevented) {
          pending = true;
          setState(form, 'loading', messages.loading);
          // A captcha can cancel navigation and later resubmit programmatically.
          // Do not disable the submitter required by Shopify's form machinery.
          form.querySelector('[data-newsletter-submit]')?.removeAttribute('disabled');
        }
        return;
      }
      event.preventDefault();
      if (pending) return;
      pending = true;
      const emailValue = preserveEmailValue(form);
      if (!input || !input.checkValidity()) {
        pending = false;
        if (input) input.setAttribute('aria-invalid', 'true');
        setState(form, 'validation', messages.validation);
        input?.focus();
        return;
      }

      setState(form, 'loading', messages.loading);
      const setRecoverableError = (state) => {
        pending = false;
        if (input) input.value = emailValue;
        const retryMessage = [messages[state], messages.retry].filter(Boolean).join(' ');
        setState(form, state, retryMessage);
      };

      let response;
      try {
        response = await window.fetch(form.action || window.location.href, {
          method: form.method || 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
      } catch (error) {
        if (disposed || controller.signal.aborted) return;
        setRecoverableError('network-error');
        return;
      }

      try {
        if (disposed || controller.signal.aborted) return;
        const providerState = await parseProviderState(form, response);
        if (disposed || controller.signal.aborted) return;
        pending = false;
        setState(form, providerState.state, providerState.message || messages[providerState.state]);
      } catch (error) {
        if (disposed || controller.signal.aborted) return;
        setRecoverableError('error');
      }
    };

    if (retry) {
      retry.hidden = !RETRY_STATES.has(initialState);
      retry.addEventListener('click', handleRetry);
    }
    form.addEventListener('input', handleInput);
    form.addEventListener('invalid', handleInvalid, true);
    form.addEventListener('submit', handleSubmit);
    if (nativeSubmit) window.addEventListener('pageshow', handlePageShow);
    form.dataset.miniamiNewsletterInitialized = 'true';
    initializedForms.set(form, {
      handleInput,
      handleInvalid,
      handleSubmit,
      handleRetry,
      handlePageShow,
      nativeSubmit,
      retry,
      controller,
      dispose: () => { disposed = true; },
    });
    setState(form, initialState, stateMessage?.textContent || messages[initialState]);
  };

  const teardown = (form) => {
    const handlers = initializedForms.get(form);
    if (!handlers) return;
    handlers.dispose();
    abortRequest(handlers.controller);
    form.removeEventListener('input', handlers.handleInput);
    form.removeEventListener('invalid', handlers.handleInvalid, true);
    form.removeEventListener('submit', handlers.handleSubmit);
    handlers.retry?.removeEventListener('click', handlers.handleRetry);
    if (handlers.nativeSubmit) window.removeEventListener('pageshow', handlers.handlePageShow);
    delete form.dataset.miniamiNewsletterInitialized;
    initializedForms.delete(form);
  };

  const initializeRoot = (root) => getForms(root).forEach(init);

  window.__miniamiNewsletterRuntime = { initializeRoot };

  document.addEventListener('shopify:section:load', (event) => initializeRoot(event.target));
  document.addEventListener('shopify:section:unload', (event) => getForms(event.target).forEach(teardown));
  initializeRoot(document);
})();
